import { Pencil, Trash2 } from 'lucide-react';
import { ForwardedRef, ReactNode, forwardRef } from 'react';
import IconButton from 'src/shared/components/ui/buttons/icon-button';
import { CommandHeading } from 'src/shared/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';
import SupportBadge from 'src/shared/components/ui/badges/support-badge';
import DynamicIcon, { DynamicIconProps, Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import { cn } from 'src/shared/lib/utils';
import { useOrg } from 'src/shared/contexts/OrgContext';

type TripleDotActionsProps = {
  children?: ReactNode;
  icon?: Icons;
  label?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  deleteTestId?: string;
  extra?: {
    text: ReactNode;
    onClick: () => void;
    icon?: DynamicIconProps['name'];
    hidden?: boolean;
    supportOnly?: boolean;
    loading?: boolean;
    keepMenuOpen?: boolean;
  }[];
  align?: 'end' | 'center' | 'start';
  className?: string;
  'data-testid'?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function TripleDotActions(
  {
    children,
    extra = [],
    icon = 'EllipsisVertical',
    align = 'end',
    label,
    onDelete,
    onEdit,
    deleteTestId,
    className,
    'data-testid': dataTestId,
    open,
    onOpenChange,
  }: TripleDotActionsProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { role } = useOrg();

  const itemClassName = cn('h-7 text-xs text-typography-grey hover:text-typography-black group gap-4 justify-start');

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        {children ? children : <IconButton className={className} icon={icon} data-testid={dataTestId} />}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={ref}
        align={align}
        onClick={(e) => e.stopPropagation()}
        className="tracking-normal rounded-sm min-w-[130px] p-0"
      >
        {label && <CommandHeading>{label}</CommandHeading>}
        <div className="p-1">
          {onEdit && (
            <DropdownMenuItem className={itemClassName} onClick={onEdit}>
              <Pencil size={13} className="stroke-typography-grey group-hover:stroke-typography-black shrink-0" />
              Edit
            </DropdownMenuItem>
          )}
          {extra
            .filter((item) => !item.hidden && (item.supportOnly ? role === 'SUPPORT' : true))
            .map((item, index) => (
              <DropdownMenuItem
                key={index}
                className={itemClassName}
                onClick={item.onClick}
                onSelect={(e) => {
                  if (item.loading || item.keepMenuOpen) {
                    e.preventDefault();
                  }
                }}
              >
                {item.icon && (
                  <DynamicIcon
                    name={item.icon}
                    size={13}
                    className="stroke-typography-grey group-hover:stroke-typography-black shrink-0"
                  />
                )}
                {item.loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 border-2 border-typography-grey border-t-transparent rounded-full animate-spin" />
                    {item.text}
                  </div>
                ) : (
                  <span className={cn('flex items-center gap-1.5', !item.icon && 'w-full justify-between')}>
                    {item.text}
                    {item.supportOnly && <SupportBadge />}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          {onDelete && (
            <DropdownMenuItem className={itemClassName} onClick={onDelete} data-testid={deleteTestId}>
              <Trash2
                size={13}
                className="group-hover:stroke-destructive transition-colors stroke-typography-grey shrink-0"
              />
              Delete
            </DropdownMenuItem>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default forwardRef<HTMLDivElement, TripleDotActionsProps>(TripleDotActions);
